import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, finalize, take } from 'rxjs';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { TeachCompensationService } from 'src/app/services/api/teach-compensation/teach-compensation.service';
import { AcademicTermModel } from 'src/app/services/models/study-period/study-period.model';
import {
  MakeupTeachingRequestReportListParams,
  MakeupTeachingRequestReportModel,
  MakeupTeachingRequestReviewTeacher,
} from 'src/app/services/models/teach-compensation/teach-compensation.model';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';

type RequestStatus = 'submitted' | 'approved' | 'rejected' | 'revision_requested' | 'draft';
type ReportStatusTab = 'all' | 'approved' | 'submitted';
type MonthFilterValue = number | 'all';
type StudyModeName = { name_th?: string | null; name_en?: string | null } | null | undefined;

interface RequestReportRow {
  id: number;
  sequence?: number | string | null;
  leaveType: string;
  leaveDate: string;
  timeRange: string;
  subjectCode: string;
  subjectName: string;
  compensationDate: string;
  compensationTime: string;
  status: RequestStatus;
  statusLabel: string;
  raw: MakeupTeachingRequestReportModel;
}

interface RequestReportDataSource {
  data: RequestReportRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

@Component({
  selector: 'app-teach-compensation-report-details',
  standalone: true,
  imports: [
    SharedModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './teach-compensation-report-details.component.html',
  styleUrl: './teach-compensation-report-details.component.scss',
})
export class TeachCompensationReportDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;
  @ViewChild('statusTemplate', { static: true })
  statusTemplate!: TemplateRef<any>;
  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<any>;

  form!: FormGroup;
  exportForm!: FormGroup;
  columns: any[] = [];
  datasource = signal<RequestReportDataSource>(this.createDataSource([]));
  tableLoading = signal(false);
  selectedRow = signal<RequestReportRow | null>(null);
  isDetailModalOpen = signal(false);
  isExportModalOpen = signal(false);
  pdfLoading = signal(false);
  pdfError = signal(false);
  pdfPreviewUrl = signal<SafeResourceUrl | null>(null);
  exportDataLoading = signal(false);
  exportHasData = signal(false);
  teacherDisplayName = signal<string | null>(null);
  activeTab = signal<ReportStatusTab>('all');
  pageSizeOptions = [10, 20, 50];
  academicTermOptions: string[] = [];
  yearOptions: string[] = [];
  statusTabs: { value: ReportStatusTab; label: string }[] = [
    { value: 'all', label: 'รายการทั้งหมด' },
    { value: 'approved', label: 'รับรองแล้ว' },
    { value: 'submitted', label: 'รอดำเนินการ' },
  ];
  monthOptions: { value: MonthFilterValue; label: string }[] = [{ value: 'all', label: 'ทั้งหมด' }];
  exportMonthOptions: { value: MonthFilterValue; label: string }[] = [{ value: 'all', label: 'ทั้งหมด' }];

  private currentPage = 1;
  private currentLimit = 10;
  private scheduleSubmissionId: number | null = null;
  private scheduleSubmissionAcademicTermId: number | null = null;
  private hasInitializedTermFilters = false;
  private currentSortParams: DatatableSortParams = { sort: 'leave_date', direction: 'asc' };
  private currentSortProp: string | null = null;
  private rawPdfPreviewUrl: string | null = null;
  private academicTerms: AcademicTermModel[] = [];
  private readonly monthLabels: Record<number, string> = {
    1: 'มกราคม',
    2: 'กุมภาพันธ์',
    3: 'มีนาคม',
    4: 'เมษายน',
    5: 'พฤษภาคม',
    6: 'มิถุนายน',
    7: 'กรกฎาคม',
    8: 'สิงหาคม',
    9: 'กันยายน',
    10: 'ตุลาคม',
    11: 'พฤศจิกายน',
    12: 'ธันวาคม',
  };
  private readonly sortFieldMap: DatatableSortFieldMap = {
    index: 'leave_date',
    leaveType: 'makeup_teaching_leave_type_id',
    leaveDate: 'leave_date',
    timeRange: 'makeup_teaching_leave_period',
    subjectCode: 'teaching_subject_code',
    subjectName: 'teaching_subject_name',
    compensationDate: 'makeup_teaching_date',
    compensationTime: 'makeup_start_time_id',
  };
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private studyPeriodService = inject(StudyPeriodService);
  private teachCompensationService = inject(TeachCompensationService);

  ngOnInit(): void {
    this.scheduleSubmissionId = Number(this.route.snapshot.paramMap.get('scheduleSubmissionId'));
    this.initForm();
    this.initColumns();
    this.listenFilters();

    if (!Number.isFinite(this.scheduleSubmissionId) || this.scheduleSubmissionId <= 0) {
      this.datasource.set(this.createDataSource([]));
      return;
    }

    this.loadFilterOptions();
    this.loadRequests(1);
  }

  ngOnDestroy(): void {
    this.revokePdfPreviewUrl();
  }

  onBack(): void {
    void this.router.navigate(['/teach-compensation-management/teach-compensation-report']);
  }

  onView(row: RequestReportRow): void {
    this.selectedRow.set(row);
    this.isDetailModalOpen.set(true);
  }

  onCloseModal(): void {
    this.isDetailModalOpen.set(false);
    this.selectedRow.set(null);
  }

  onOpenExportModal(): void {
    this.exportForm.patchValue(this.form.getRawValue(), { emitEvent: false });
    this.updateMonthOptionsForForm(this.exportForm, true);
    this.checkExportDataAvailability();
    this.refreshPdfPreviewUrl();
    this.isExportModalOpen.set(true);
  }

  onCloseExportModal(): void {
    this.isExportModalOpen.set(false);
    this.revokePdfPreviewUrl();
  }

  onDownloadExportPdf(): void {
    if (this.scheduleSubmissionId == null || this.exportDataLoading() || !this.exportHasData()) {
      return;
    }

    this.teachCompensationService.downloadMakeupTeachingRequestReportPdf(this.buildExportPdfParams())
      .pipe(take(1))
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'makeup-teaching-request-report.pdf';
          link.click();
          window.URL.revokeObjectURL(url);
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  onTabChange(tab: ReportStatusTab): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.loadRequests(1);
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.datasource().total_page || page === this.currentPage) {
      return;
    }

    this.loadRequests(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadRequests(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.currentSortProp = event.sort?.sorts?.[0]?.prop ?? null;
    this.loadRequests(1);
  }

  getDisplayRowIndex(rowIndex: number): number {
    const datasource = this.datasource();

    if (this.currentSortProp === 'index' && this.currentSortParams.direction === 'desc') {
      return datasource.total - datasource.offset - rowIndex;
    }

    return datasource.offset + rowIndex + 1;
  }

  getTeacherName(row: RequestReportRow | null): string {
    return this.formatTeacherDisplayName(row?.raw.teacher, row?.raw.academic_rank?.name) || '-';
  }

  getDepartmentName(row: RequestReportRow | null): string {
    return this.firstPresent(
      row?.raw.department?.name_th,
      row?.raw.department?.name,
      row?.raw.teacher?.department?.name_th,
      row?.raw.teacher?.department?.name,
    );
  }

  getSelectedSubjectCode(row: RequestReportRow | null): string {
    const item = row?.raw;
    return item?.subject?.code ??
      item?.subject_schedule_group?.subject?.code ??
      item?.subject_schedule_group?.code ??
      '-';
  }

  getSelectedSubjectName(row: RequestReportRow | null): string {
    const item = row?.raw;
    return item?.subject?.name ??
      item?.subject_schedule_group?.subject?.name ??
      item?.subject_schedule_group?.name ??
      '-';
  }

  getSelectedCurriculum(row: RequestReportRow | null): string {
    const item = row?.raw;
    return item?.subject?.curriculum_type?.name ??
      item?.subject_schedule_group?.subject?.curriculum_type?.name ??
      item?.subject_schedule_group?.curriculum_type?.name ??
      '-';
  }

  getSelectedStudyMode(row: RequestReportRow | null): string {
    const item = row?.raw;

    return this.firstPresent(
      this.getStudyModeName(item?.subject?.study_mode),
      this.getStudyModeName(item?.subject_schedule_group?.subject?.study_mode),
      this.getStudyModeName(item?.subject_schedule_group?.study_mode),
    );
  }

  getSelectedCourseUnit(row: RequestReportRow | null): string {
    const item = row?.raw;
    return item?.subject?.courseunit ??
      item?.subject_schedule_group?.subject?.courseunit ??
      item?.subject_schedule_group?.courseunit ??
      '-';
  }

  formatDetailDate(value?: string | null): string {
    return this.formatDate(value);
  }

  formatDetailTimeRange(start?: string | null, end?: string | null): string {
    return this.formatTimeRange(start, end);
  }

  private initForm(): void {
    this.form = this.fb.group({
      academicTerm: [null],
      year: [null],
      month: ['all'],
    });
    this.exportForm = this.fb.group({
      academicTerm: [null],
      year: [null],
      month: ['all'],
    });
  }

  private listenFilters(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(150),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.updateMonthOptionsForForm(this.form);
        this.loadRequests(1);
      });

    this.exportForm.valueChanges
      .pipe(
        debounceTime(150),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.updateMonthOptionsForForm(this.exportForm, true);
        if (this.isExportModalOpen()) {
          this.checkExportDataAvailability();
          this.refreshPdfPreviewUrl();
        }
      });
  }

  private loadFilterOptions(): void {
    this.studyPeriodService.getAcademicTerms({ limit: 200 })
      .pipe(take(1))
      .subscribe({
        next: (terms) => {
          this.academicTerms = terms;
          this.academicTermOptions = this.buildAcademicTermOptions(terms);
          this.yearOptions = this.buildAcademicYearOptions(terms);
          this.initializeTermFiltersFromSchedule();
          this.updateMonthOptionsForForm(this.form);
          this.updateMonthOptionsForForm(this.exportForm, true);
        },
        error: (err) => {
          console.error(err);
          this.monthOptions = [{ value: 'all', label: 'ทั้งหมด' }];
          this.exportMonthOptions = [{ value: 'all', label: 'ทั้งหมด' }];
        },
      });
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 72,
        minWidth: 72,
        maxWidth: 72,
      },
     
      {
        prop: 'leaveDate',
        name: 'วันที่ลา / วันที่หยุด',
        sortable: true,
        width: 170,
        minWidth: 170,
        headerClass: 'teach-compensation-report-details-wrap-header',
        cellClass: 'teach-compensation-report-details-wrap-cell',
      },
       {
        prop: 'leaveType',
        name: 'ประเภทการลา / หยุด',
        sortable: false,
        width: 150,
        minWidth: 150,
      },
      {
        prop: 'timeRange',
        name: 'ช่วงเวลาการลา',
        sortable: true,
        width: 180,
        minWidth: 180,
      },
      {
        prop: 'subjectCode',
        name: 'รหัสวิชา',
        sortable: true,
        width: 100,
        minWidth: 100,
      },
      {
        prop: 'subjectName',
        name: 'ชื่อรายวิชา',
        sortable: true,
        width: 190,
        minWidth: 190,
        headerClass: 'teach-compensation-report-details-wrap-header',
        cellClass: 'teach-compensation-report-details-wrap-cell',
      },
      {
        prop: 'compensationDate',
        name: 'วันที่ขอสอนชดเชย',
        sortable: true,
        width: 140,
        minWidth: 120,
      },
      {
        prop: 'compensationTime',
        name: 'เวลาสอนชดเชย',
        sortable: true,
        width: 120,
        minWidth: 120,
      },
      {
        prop: 'status',
        name: 'สถานะ:',
        sortable: false,
        cellTemplate: this.statusTemplate,
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        cellClass: 'text-center schedule-status-cell',
        headerClass: 'text-center schedule-status-header',
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        cellClass: 'text-center schedule-action-cell',
        headerClass: 'text-center schedule-action-header',
      },
    ];
  }

  private loadRequests(page = this.currentPage): void {
    if (this.scheduleSubmissionId == null) {
      return;
    }

    this.currentPage = page;
    this.tableLoading.set(true);

    this.teachCompensationService.getMakeupTeachingRequestReportList({
      ...this.buildFilterParams(),
      page,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    }).pipe(
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => {
        const rows = response.data.map((item) => this.mapRequestToRow(item));
        const teacherName = this.formatTeacherDisplayName(response.teacher, response.academic_rank?.name) ||
          this.formatTeacherDisplayName(rows[0]?.raw.teacher, rows[0]?.raw.academic_rank?.name);

        this.scheduleSubmissionAcademicTermId = response.schedule_submission?.academic_term_id ?? null;
        this.initializeTermFiltersFromSchedule();
        this.teacherDisplayName.set(teacherName || null);

        this.datasource.set(this.createDataSource(
          rows,
          response.page,
          response.limit,
          response.total,
          response.total_pages,
          (response.page - 1) * response.limit,
        ));
      },
      error: (err) => {
        console.error(err);
        this.teacherDisplayName.set(null);
        this.datasource.set(this.createDataSource([], page, this.currentLimit));
      },
    });
  }

  private buildFilterParams(): MakeupTeachingRequestReportListParams {
    const { academicTerm, year, month } = this.form.getRawValue();
    const params: MakeupTeachingRequestReportListParams = {
      schedule_submission_id: this.scheduleSubmissionId,
      ...this.buildAcademicTermParams(academicTerm, year),
      month: this.normalizeMonthFilterValue(month),
    };

    if (this.activeTab() !== 'all') {
      params.status = this.activeTab();
    }

    return params;
  }

  private mapRequestToRow(item: MakeupTeachingRequestReportModel): RequestReportRow {
    const status = this.normalizeStatus(item.status);
    const subjectCode = this.getSubjectCode(item);
    const subjectName = this.getSubjectName(item);

    return {
      id: item.id,
      sequence: item.sequence,
      leaveType: item.makeup_teaching_leave_type?.name ?? '-',
      leaveDate: this.formatDate(item.leave_date),
      timeRange: this.formatLeavePeriod(item),
      subjectCode,
      subjectName,
      compensationDate: this.formatDate(item.makeup_teaching_date),
      compensationTime: this.formatTimeRange(
        item.makeup_start_time?.label ?? item.makeup_start_time?.time_value,
        item.makeup_end_time?.label ?? item.makeup_end_time?.time_value,
      ),
      status,
      statusLabel: this.getStatusLabel(status),
      raw: item,
    };
  }

  private createDataSource(
    rows: RequestReportRow[],
    page = 1,
    limit = 10,
    total = rows.length,
    totalPage = Math.max(Math.ceil(total / limit), 1),
    offset = (page - 1) * limit,
  ): RequestReportDataSource {
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);

    return {
      data: rows,
      limit,
      offset: normalizedPage === page ? offset : (normalizedPage - 1) * limit,
      page: normalizedPage,
      total,
      total_page: totalPage,
    };
  }

  private initializeTermFiltersFromSchedule(): void {
    if (this.hasInitializedTermFilters || this.scheduleSubmissionAcademicTermId == null || this.academicTerms.length === 0) {
      return;
    }

    const term = this.findAcademicTermById(this.scheduleSubmissionAcademicTermId);

    if (term == null) {
      return;
    }

    const value = {
      academicTerm: String(term.name),
      year: String(this.toBuddhistYear(term.year)),
      month: 'all',
    };

    this.form.patchValue(value, { emitEvent: false });
    this.exportForm.patchValue(value, { emitEvent: false });
    this.updateMonthOptionsForForm(this.form);
    this.updateMonthOptionsForForm(this.exportForm, true);
    this.hasInitializedTermFilters = true;
  }

  private buildAcademicTermParams(
    academicTerm: string | number | null,
    year: string | number | null,
  ): Partial<MakeupTeachingRequestReportListParams> {
    const normalizedAcademicTerm = String(academicTerm ?? '').trim();
    const normalizedYear = String(year ?? '').trim();
    const params: Partial<MakeupTeachingRequestReportListParams> = {};

    if (normalizedAcademicTerm !== '') {
      params.academic_term_name = normalizedAcademicTerm;
    }

    const numericYear = Number(normalizedYear);
    if (Number.isFinite(numericYear) && numericYear > 0) {
      params.academic_term_year = this.toChristianYear(numericYear);
    }

    return params;
  }

  private refreshPdfPreviewUrl(): void {
    if (this.scheduleSubmissionId == null) {
      this.revokePdfPreviewUrl();
      return;
    }

    this.pdfLoading.set(true);
    this.pdfError.set(false);

    this.teachCompensationService.downloadMakeupTeachingRequestReportPdf(this.buildExportPdfParams())
      .pipe(finalize(() => this.pdfLoading.set(false)))
      .subscribe({
        next: (blob) => {
          this.revokePdfPreviewUrl();
          this.rawPdfPreviewUrl = window.URL.createObjectURL(blob);
          this.pdfPreviewUrl.set(
            this.sanitizer.bypassSecurityTrustResourceUrl(`${this.rawPdfPreviewUrl}#toolbar=0&navpanes=0`),
          );
        },
        error: (err) => {
          console.error(err);
          this.revokePdfPreviewUrl();
          this.pdfError.set(true);
        },
      });
  }

  private checkExportDataAvailability(): void {
    if (this.scheduleSubmissionId == null) {
      this.exportHasData.set(false);
      return;
    }

    this.exportDataLoading.set(true);

    this.teachCompensationService.getMakeupTeachingRequestReportList({
      ...this.buildExportPdfParams(),
      page: 1,
      limit: 1,
    }).pipe(
      finalize(() => this.exportDataLoading.set(false)),
    ).subscribe({
      next: (response) => {
        this.exportHasData.set(response.total > 0);
      },
      error: (err) => {
        console.error(err);
        this.exportHasData.set(false);
      },
    });
  }

  private buildExportPdfParams(): MakeupTeachingRequestReportListParams {
    const { academicTerm, year, month } = this.exportForm.getRawValue();

    return {
      schedule_submission_id: this.scheduleSubmissionId,
      ...this.buildAcademicTermParams(academicTerm, year),
      month: this.normalizeMonthFilterValue(month),
    };
  }

  private revokePdfPreviewUrl(): void {
    if (this.rawPdfPreviewUrl != null) {
      window.URL.revokeObjectURL(this.rawPdfPreviewUrl);
      this.rawPdfPreviewUrl = null;
    }

    this.pdfPreviewUrl.set(null);
  }

  private updateMonthOptionsForForm(form: FormGroup, forExport = false): void {
    const selectedTerm = this.findAcademicTerm(
      String(form.get('academicTerm')?.value ?? ''),
      String(form.get('year')?.value ?? ''),
    );
    const options = this.buildMonthOptions(selectedTerm);
    const selectedMonth = form.get('month')?.value;

    if (forExport) {
      this.exportMonthOptions = options;
    } else {
      this.monthOptions = options;
    }

    if (selectedMonth !== 'all' && !options.some((option) => option.value === Number(selectedMonth))) {
      form.patchValue({ month: 'all' }, { emitEvent: false });
    }
  }

  private buildMonthOptions(term?: AcademicTermModel): { value: MonthFilterValue; label: string }[] {
    const options: { value: MonthFilterValue; label: string }[] = [{ value: 'all', label: 'ทั้งหมด' }];

    if (term == null) {
      return options;
    }

    const start = this.parseDateOnly(term.study_start_date);
    const end = this.parseDateOnly(term.study_end_date);

    if (start == null || end == null || start > end) {
      return options;
    }

    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);

    while (cursor <= endMonth) {
      const month = cursor.getMonth() + 1;

      options.push({
        value: month,
        label: this.monthLabels[month],
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }

    return options;
  }

  private normalizeMonthFilterValue(value: unknown): number | null {
    if (value === 'all' || value == null || value === '') {
      return null;
    }

    const month = Number(value);

    return Number.isFinite(month) ? month : null;
  }

  private findAcademicTerm(
    academicTermName: string,
    academicYear: string,
  ): AcademicTermModel | undefined {
    return this.academicTerms.find((term) => (
      String(term.name) === academicTermName &&
      String(this.toBuddhistYear(term.year)) === String(academicYear)
    ));
  }

  private findAcademicTermById(id: number | null): AcademicTermModel | undefined {
    if (id == null) {
      return undefined;
    }

    return this.academicTerms.find((term) => term.id === id);
  }

  private buildAcademicTermOptions(terms: AcademicTermModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => String(term.name).trim())
        .filter(Boolean),
    )).sort((a, b) => a.localeCompare(b, 'th'));
  }

  private buildAcademicYearOptions(terms: AcademicTermModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => this.toBuddhistYear(term.year))
        .filter((year) => Number.isFinite(year))
        .map((year) => String(year)),
    )).sort((a, b) => Number(b) - Number(a));
  }

  private parseDateOnly(value?: string | null): Date | null {
    if (value == null || value === '') {
      return null;
    }

    const [year, month, day] = value.split('-').map((part) => Number(part));

    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  private getSubjectCode(item: MakeupTeachingRequestReportModel): string {
    return item.subject?.code ??
      item.subject_schedule_group?.subject?.code ??
      item.subject_schedule_group?.code ??
      '-';
  }

  private getSubjectName(item: MakeupTeachingRequestReportModel): string {
    return item.subject?.name ??
      item.subject_schedule_group?.subject?.name ??
      item.subject_schedule_group?.name ??
      '-';
  }

  private formatLeavePeriod(item: MakeupTeachingRequestReportModel): string {
    const period = item.makeup_teaching_leave_period;
    const periodName = period?.name;
    const startTime = this.formatTime(period?.start_time);
    const endTime = this.formatTime(period?.end_time);

    if (periodName != null && periodName !== '' && startTime !== '-' && endTime !== '-') {
      return `${periodName} (${startTime} - ${endTime} น.)`;
    }

    return periodName ?? '-';
  }

  private formatDate(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = this.toBuddhistYear(date.getFullYear());

    return `${day}/${month}/${year}`;
  }

  private formatTimeRange(start?: string | null, end?: string | null): string {
    const startTime = this.formatTime(start);
    const endTime = this.formatTime(end);

    if (startTime === '-' || endTime === '-') {
      return '-';
    }

    return `${startTime} - ${endTime} น.`;
  }

  private formatTime(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const timeMatch = /T(\d{2}):(\d{2})/.exec(value);

    if (timeMatch != null) {
      return `${Number(timeMatch[1])}:${timeMatch[2]}`;
    }

    const timeParts = value.split(':');

    if (timeParts.length < 2) {
      return value;
    }

    const [hours, minutes] = timeParts;

    return `${Number(hours)}:${minutes}`;
  }

  private normalizeStatus(status?: string | null): RequestStatus {
    if (status === 'approved' || status === 'rejected' || status === 'revision_requested' || status === 'draft') {
      return status;
    }

    return 'submitted';
  }

  private getStatusLabel(status: RequestStatus): string {
    const labels: Record<RequestStatus, string> = {
      draft: 'แบบร่าง',
      submitted: 'รอดำเนินการ',
      approved: 'รับรองแล้ว',
      rejected: 'ไม่อนุมัติ',
      revision_requested: 'ตีกลับให้แก้ไข',
    };

    return labels[status];
  }

  private getStudyModeName(studyMode: StudyModeName): string | null | undefined {
    return studyMode?.name_th ?? studyMode?.name_en;
  }

  private firstPresent(...values: (string | null | undefined)[]): string {
    const value = values.find((item) => item != null && item !== '');

    return value ?? '-';
  }

  private formatTeacherDisplayName(
    teacher?: MakeupTeachingRequestReviewTeacher | null,
    academicRankName?: string | null,
  ): string {
    const teacherName = teacher?.full_name?.trim();
    if (teacherName == null || teacherName === '') {
      return '';
    }

    const academicRank = (academicRankName ?? teacher?.academic_rank?.name ?? '').trim();

    return academicRank ? `${academicRank} ${teacherName}` : teacherName;
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }

  private toChristianYear(year: number): number {
    return year > 2400 ? year - 543 : year;
  }
}
